// Code generated by apic; DO NOT EDIT.
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useMemo } from "react";
import type { RestEndpointMeta, SchemaMeta } from "./types";
import type { ApicClient } from "../react/client";
import { AuthBadge } from "./AuthBadge";
import { SchemaView } from "./SchemaView";
import { CodeSamples } from "./CodeSamples";
import { TryPanel } from "./TryPanel";
import { useExplorerClient } from "./useExplorerClient";
import type { SampleContext } from "./codegen/curl";

/**
 * EndpointCard renders a single REST endpoint's method, path, description,
 * authentication policy (via AuthBadge), and request/response schemas (via
 * SchemaView when the schema name is non-null and present in the schemas map).
 * Below the schemas, a <CodeSamples> tab strip shows curl / Go / Python /
 * TypeScript snippets generated from the endpoint metadata and the supplied
 * baseUrl context.
 * Below the code samples, a <TryPanel> provides a live "Try it" panel that
 * exercises the endpoint against the configured baseUrl.
 *
 * The card is collapsed by default: only the header row (method / path /
 * responseKind badge / AuthBadge) is visible until the user expands it.  This
 * keeps SchemaView, CodeSamples, and TryPanel from mounting until needed,
 * avoiding unnecessary computation for large endpoint lists.
 *
 * Props:
 *   endpoint  - the RestEndpointMeta to display.
 *   schemas   - optional map of schema name to SchemaMeta (from ServiceMetadata.schemas).
 *   baseUrl   - optional base URL prefix passed into the code-sample builders.
 *   client    - optional pre-built ApicClient; when absent, one is created
 *               via useExplorerClient(baseUrl).
 *   className - extra CSS class appended to the root element.
 *
 * No web-global dependencies at module load time (RN-safe).
 */
export const EndpointCard = React.memo(function EndpointCard(props: {
  endpoint: RestEndpointMeta;
  schemas?: Record<string, SchemaMeta>;
  baseUrl?: string;
  client?: ApicClient;
  className?: string;
}): JSX.Element {
  const { endpoint, schemas, baseUrl, client: clientProp, className } = props;
  const root = ["apic-ui-endpoint-card", className].filter(Boolean).join(" ");

  // Collapsed by default so heavy sub-components (SchemaView, CodeSamples,
  // TryPanel) only mount when the user expands the card.
  const [expanded, setExpanded] = useState<boolean>(false);

  // Build a client via the hook when the caller doesn't supply one.
  // useExplorerClient memoizes on baseUrl and reads the ambient token on each
  // request — safe to call unconditionally (no conditional hook call).
  const ownClient = useExplorerClient(baseUrl);
  const client: ApicClient = clientProp !== undefined ? clientProp : ownClient;

  const reqSchema =
    endpoint.requestSchema !== null && schemas !== undefined
      ? (schemas[endpoint.requestSchema] ?? null)
      : null;

  const respSchema =
    endpoint.responseSchema !== null && schemas !== undefined
      ? (schemas[endpoint.responseSchema] ?? null)
      : null;

  // Memoize the SampleContext so it is only recomputed when baseUrl changes.
  // Path params default to empty; the body, token, and apiKey are unset so
  // builders use their placeholder values ("<token>", "<api-key>") which is
  // safe for display.
  const sampleCtx: SampleContext = useMemo(() => ({
    baseUrl: baseUrl !== undefined && baseUrl !== "" ? baseUrl : "https://api.example.com",
    pathParams: {},
  }), [baseUrl]);

  // True when the response is binary or streaming — used for the badge and to
  // suppress JSON-parse attempts in TryPanel.
  const isBinaryResponse = endpoint.responseKind === "blob" || endpoint.responseKind === "arrayBuffer";

  return (
    <div className={root + (expanded ? " apic-ui-endpoint-card--expanded" : " apic-ui-endpoint-card--collapsed")}>
      {/* Disclosure header — always visible; clicking toggles expanded */}
      <button
        type="button"
        className="apic-ui-endpoint-card-header apic-ui-endpoint-card-toggle"
        aria-expanded={expanded}
        onClick={() => { setExpanded((prev) => !prev); }}
      >
        {/* Method badge */}
        <span className={"apic-ui-method-badge apic-ui-method-badge--" + endpoint.method.toLowerCase()}>
          {endpoint.method}
        </span>
        {/* Path */}
        <span className="apic-ui-endpoint-path">{endpoint.path}</span>

        {/* responseKind badge — only shown for non-JSON responses */}
        {isBinaryResponse && (
          <span className="apic-ui-chip apic-ui-chip--response-kind">
            {endpoint.responseKind === "arrayBuffer" ? "streaming" : "binary"}
          </span>
        )}

        {/* Authentication policy — always shown in header for quick scanning */}
        <AuthBadge auth={endpoint.auth} className="apic-ui-endpoint-auth" />

        {/* Collapse indicator */}
        <span className="apic-ui-endpoint-card-chevron" aria-hidden="true">
          {expanded ? "▲" : "▼"}
        </span>
      </button>

      {/* Expanded body — only mounted when expanded */}
      {expanded && (
        <div className="apic-ui-endpoint-card-body">
          {/* Description — only rendered when non-empty */}
          {endpoint.description !== "" && (
            <p className="apic-ui-endpoint-description">{endpoint.description}</p>
          )}

          {/* Request schema */}
          {endpoint.requestSchema !== null && (
            <div className="apic-ui-endpoint-schema apic-ui-endpoint-schema--request">
              <span className="apic-ui-schema-label">{"Request"}</span>
              <SchemaView schema={reqSchema} />
            </div>
          )}

          {/* Response schema */}
          {endpoint.responseSchema !== null && (
            <div className="apic-ui-endpoint-schema apic-ui-endpoint-schema--response">
              <span className="apic-ui-schema-label">{"Response"}</span>
              <SchemaView schema={respSchema} />
            </div>
          )}

          {/* CodeSamples — curl / Go / Python / TypeScript tabs */}
          <CodeSamples
            endpoint={endpoint}
            ctx={sampleCtx}
            className="apic-ui-endpoint-samples"
          />

          {/* TryPanel — live REST Try-it */}
          <TryPanel
            endpoint={endpoint}
            client={client}
            {...(baseUrl !== undefined ? { baseUrl } : {})}
            className="apic-ui-endpoint-try"
          />
        </div>
      )}
    </div>
  );
});
